<template>
    <el-container class="list-page time-page">
        <el-header class="list-page-header time-page-header">
            <div>
                <h1 class="list-page-title">{{$t('time.pageTitle')}}</h1>
                <el-radio-group v-model="billed" size="medium" @change="resetPage">
                    <el-radio-button :label="0">{{$t('time.unbilled')}}</el-radio-button>
                    <el-radio-button :label="1">{{$t('time.billed')}}</el-radio-button>
                </el-radio-group>
            </div>
            <div class="list-page-search">
                <el-input prefix-icon="el-icon-search" v-model="searchContent"
                          :placeholder="$t('time.searchPlaceholder')"></el-input>
            </div>
            <div class="time-header-actions">
                <el-button icon="el-icon-s-operation" @click="settingsVisible = true">{{$t('time.filterAndSort')}}</el-button>
                <el-button class="list-page-add-button" type="primary" @click="goCreate">
                    <i class="el-icon-plus"></i><span>{{$t('time.addTime')}}</span>
                </el-button>
            </div>
        </el-header>
        <el-main class="list-page-main" v-loading="loading">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <div v-else-if="sections.length === 0" class="time-empty">
                <i class="el-icon-time"></i>
                <h2>{{$t('time.noTime')}}</h2>
                <p>{{$t('time.noTimeDescription')}}</p>
            </div>
            <section v-for="section in sections" :key="section.title" class="time-section">
                <div class="time-section-title">
                    <span>{{section.title === '' ? $t('time.noClient') : section.title}}</span>
                    <span>{{formatDuration(section.totalMinutes)}}</span>
                </div>
                <div class="list-table-card">
                    <el-table class="list-table" :data="section.times" @row-click="openTime">
                        <el-table-column :label="$t('common.client')" min-width="190">
                            <template slot-scope="scope">
                                <strong>{{scope.row.clientName === '' ? $t('time.noClient') : scope.row.clientName}}</strong>
                                <span class="time-description">{{scope.row.timeDescription}}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('common.date')" min-width="180">
                            <template slot-scope="scope">{{formatDate(scope.row.startDate)}}</template>
                        </el-table-column>
                        <el-table-column :label="$t('time.duration')" min-width="130">
                            <template slot-scope="scope">{{formatDuration(scope.row.totalMinutes)}}</template>
                        </el-table-column>
                        <el-table-column :label="$t('common.rate')" min-width="150" align="right">
                            <template slot-scope="scope">
                                <span v-if="scope.row.showRateName === 1">{{scope.row.rateName}} · </span>{{money(scope.row.rate)}}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('common.total')" min-width="150" align="right">
                            <template slot-scope="scope"><strong>{{money(scope.row.total)}}</strong></template>
                        </el-table-column>
                    </el-table>
                </div>
            </section>
            <div v-if="totalCount > 0" class="list-pagination">
                <el-pagination
                        @size-change="changePageSize"
                        @current-change="changePage"
                        :current-page="currentPage"
                        :page-sizes="[50, 100, 200, 400]"
                        :page-size="pageSize"
                        layout="total, sizes, prev, pager, next, jumper"
                        :total="totalCount">
                </el-pagination>
            </div>
        </el-main>

        <el-dialog :title="$t('time.filterAndSort')" :visible.sync="settingsVisible" width="560px"
                   :close-on-click-modal="false">
            <el-form label-position="top" class="time-settings-form">
                <el-form-item :label="$t('time.year')">
                    <el-select v-model="settings.timeFilterYear" style="width: 100%">
                        <el-option v-for="year in yearOptions" :key="year" :label="year === allYears ? $t('invoice.allYears') : year" :value="year"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item :label="$t('time.groupBy')">
                    <el-radio-group v-model="settings.timeGroupBy">
                        <el-radio :label="0">{{$t('time.yearly')}}</el-radio>
                        <el-radio :label="1">{{$t('time.quarterly')}}</el-radio>
                        <el-radio :label="2">{{$t('time.monthly')}}</el-radio>
                        <el-radio :label="4">{{$t('common.client')}}</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item :label="$t('time.sortBy')">
                    <el-radio-group v-model="settings.timeSortType">
                        <el-radio :label="0">{{$t('common.client')}}</el-radio>
                        <el-radio :label="1">{{$t('common.date')}}</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item :label="$t('time.order')">
                    <el-radio-group v-model="settings.timeSortAscending">
                        <el-radio :label="1">{{$t('time.ascending')}}</el-radio>
                        <el-radio :label="0">{{$t('time.descending')}}</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button @click="settingsVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="savingSettings" @click="saveSettings">{{$t('common.save')}}</el-button>
            </div>
        </el-dialog>
    </el-container>
</template>

<script>
    import moment from 'moment-timezone'
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {getCurrentCompany, replaceStoredCompany} from '@/utils/company'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {
        ALL_YEARS,
        timeGroupKey,
        validateTimePreferences
    } from '@/utils/timeTracker'

    export default {
        data() {
            const company = getCurrentCompany()
            return {
                allYears: ALL_YEARS,
                billed: 0,
                company,
                loading: true,
                loadError: '',
                rawTimes: [],
                yearList: [],
                searchContent: '',
                currentPage: 1,
                pageSize: 50,
                totalCount: 0,
                settingsVisible: false,
                savingSettings: false,
                settings: {
                    timeFilterYear: company.timeFilterYear,
                    timeGroupBy: company.timeGroupBy,
                    timeSortType: company.timeSortType,
                    timeSortAscending: company.timeSortAscending
                },
                userSetting: JSON.parse(localStorage.getItem('userSetting'))
            }
        },
        computed: {
            yearOptions() {
                const years = this.yearList.slice()
                if (this.settings.timeFilterYear !== ALL_YEARS && !years.includes(this.settings.timeFilterYear)) {
                    years.push(this.settings.timeFilterYear)
                    years.sort((left, right) => Number(right) - Number(left))
                }
                return [ALL_YEARS].concat(years)
            },
            sections() {
                if (this.loadError !== '') {
                    return []
                }
                const groups = new Map()
                this.rawTimes.forEach(time => {
                    const title = timeGroupKey(time, this.settings.timeGroupBy)
                    if (!groups.has(title)) {
                        groups.set(title, [])
                    }
                    groups.get(title).push(time)
                })
                return Array.from(groups.entries()).map(([title, times]) => ({
                    title,
                    times,
                    totalMinutes: times.reduce((sum, time) => sum + time.totalMinutes, 0)
                }))
            }
        },
        methods: {
            restorePagination() {
                const pagination = readListPagination('time')
                if (pagination != null) {
                    this.currentPage = pagination.currentPage
                    this.pageSize = pagination.pageSize
                }
            },
            savePagination() {
                writeListPagination('time', this.currentPage, this.pageSize)
            },
            queryParams() {
                const params = {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    userWebCommonQuery: this.searchContent,
                    orderName: this.settings.timeSortType === 0 ? 'clientName' : 'startDate',
                    orderTpe: this.settings.timeSortAscending === 1 ? 1 : 2,
                    companyUUID: this.company.innerUUID,
                    billed: this.billed,
                    timeZone: moment.tz.guess()
                }
                if (this.settings.timeFilterYear !== ALL_YEARS) {
                    const year = Number(this.settings.timeFilterYear)
                    if (!Number.isInteger(year) || year < 1970 || year > 3000) {
                        throw new Error('Time filter year is invalid')
                    }
                    const start = moment.tz(`${year}-01-01`, 'YYYY-MM-DD', params.timeZone).startOf('day')
                    params.periodStartTime = String(start.unix())
                    params.periodEndTime = String(start.clone().add(1, 'year').unix())
                }
                return params
            },
            queryTimes() {
                this.loading = true
                this.loadError = ''
                this.$axios.post('query_time_tracker_list_user_web', {params: this.queryParams()}).then(response => {
                    if (response.data.errorCode !== 0) {
                        throw new Error(response.data.errorMsg)
                    }
                    if (!Array.isArray(response.data.dataList) || !Array.isArray(response.data.yearList)
                        || !Number.isInteger(response.data.totalCount) || response.data.totalCount < 0
                        || response.data.yearList.some(year => !Number.isInteger(year))) {
                        throw new Error('Time list response is invalid')
                    }
                    validateTimePreferences(this.company)
                    this.rawTimes = response.data.dataList
                    this.yearList = response.data.yearList.map(String)
                    this.totalCount = response.data.totalCount
                }).catch(error => {
                    this.loadError = error.message
                }).finally(() => {
                    this.loading = false
                })
            },
            resetPage() {
                this.currentPage = 1
                this.savePagination()
                this.queryTimes()
            },
            changePage(page) {
                this.currentPage = page
                this.savePagination()
                this.queryTimes()
            },
            changePageSize(pageSize) {
                this.currentPage = 1
                this.pageSize = pageSize
                this.savePagination()
                this.queryTimes()
            },
            goCreate() {
                this.$router.push('/timeCreate')
            },
            openTime(row) {
                this.$router.push({path: '/timeUpdate', query: {innerUUID: row.innerUUID}})
            },
            formatDate(timestamp) {
                return moment.unix(Number(timestamp)).format('MMM D, YYYY · h:mm A')
            },
            formatDuration(minutes) {
                return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
            },
            money(value) {
                return xiaoshudianfenge(value, this.userSetting)
            },
            saveSettings() {
                const candidate = Object.assign({}, this.company, this.settings)
                validateTimePreferences(candidate)
                this.savingSettings = true
                this.$axios.post('update_company', {params: Object.assign({innerUUID: this.company.innerUUID}, this.settings)}).then(response => {
                    if (response.data.errorCode !== 0) {
                        return
                    }
                    if (response.data.objectData == null || response.data.objectData.innerUUID !== this.company.innerUUID) {
                        throw new Error('Updated company response is invalid')
                    }
                    this.company = response.data.objectData
                    replaceStoredCompany(this.company)
                    this.settingsVisible = false
                    this.$message.success(this.$t('common.updateSuccess'))
                    this.currentPage = 1
                    this.savePagination()
                    this.queryTimes()
                }).catch(error => {
                    this.$message.error(error.message)
                }).finally(() => {
                    this.savingSettings = false
                })
            }
        },
        mounted() {
            if (this.company == null) {
                throw new Error('Current company is required')
            }
            validateTimePreferences(this.company)
            this.restorePagination()
            this.queryTimes()
            this.$watch('searchContent', debounce(() => {
                this.currentPage = 1
                this.savePagination()
                this.queryTimes()
            }, 500))
        }
    }
</script>

<style scoped>
    .time-page-header { grid-template-columns: minmax(250px, 1fr) minmax(280px, 420px) auto; }
    .time-page-header > div:first-child { display: flex; align-items: center; gap: 26px; }
    .time-header-actions { display: flex; gap: 12px; justify-content: flex-end; }
    .time-section { margin-bottom: 26px; }
    .time-section-title { display: flex; justify-content: space-between; padding: 0 4px 10px; color: #52627c; font-size: 14px; font-weight: 700; }
    .time-description { display: block; margin-top: 5px; color: #8a97aa; font-weight: 400; }
    .time-empty { min-height: 430px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #8a97aa; text-align: center; }
    .time-empty i { font-size: 54px; color: #aab6c8; }
    .time-empty h2 { margin: 18px 0 6px; color: #263653; }
    .time-empty p { margin: 0; }
    .time-settings-form .el-radio { margin: 0 22px 12px 0; }
    @media (max-width: 1050px) {
        .time-page-header { grid-template-columns: 1fr; height: auto !important; gap: 18px; }
        .time-header-actions { justify-content: flex-start; }
    }
</style>
